import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import QuizView from './QuizView';
import QuizResult from './components/QuizResult';
import {
  actions as quizActions,
  QuizStateType,
} from '@/components/Player/modules/reducers/quiz';

import './Quiz.scss';

import {
  ElearningPlayerStateType,
  actions as ElearningPlayerActions,
} from '@/components/Player/modules/reducers/elearningPlayer';
import { QuizDataType } from '@/modules/types/quiz';
import { getCompulsoryQuestions, getEnabledQuestions } from '@/lib/util';
import { Alert } from 'elmo-elements';
import { FailBehaviour } from '@/modules/types/choice';

const Quiz = (): ReactElement => {
  const dispatch = useDispatch();

  const {
    isLive,
    content,
    handlers: {
      submitAssessment,
      submitResult,
      getQuestions,
      moveToFrame,
      startAssessment,
    },
  }: ElearningPlayerStateType = useSelector(
    (state: { elearningPlayer: ElearningPlayerStateType }) =>
      state.elearningPlayer,
  );
  const {
    quiz: quizData,
    frames,
    quizStartAttemptData,
    quizGetAttemptData,
  } = content;

  const quiz: QuizStateType = useSelector(
    (state: { quiz: QuizStateType }) => state.quiz,
  );

  const [currentQuestionNo, setCurrentQuestionNo] = useState(1);

  const handler = (lastQuestion: boolean) => {
    if (lastQuestion) {
      const submitQuizResultSuccessCallbackFn = (payload: any) => {
        dispatch({
          type: quizActions.submitQuizResultSuccess.type,
          payload,
        });
      };
      // To set the loading state @todo: use local state than global
      submitAssessment(
        {
          quizId: quizData?.id,
        },
        submitQuizResultSuccessCallbackFn,
      );

      // Also do the submit result to submit results to TMS for live version
      if (isLive) {
        const submitResultCallbackFn = (payload: any) => {
          // @todo: refactor this to make the callback optional if it's not required
        };

        submitResult(quizStartAttemptData, submitResultCallbackFn);
      }
    } else {
      setCurrentQuestionNo(currentQuestionNo + 1);
    }
  };

  const tryAgainSubmitHandler = () => {
    dispatch({
      type: quizActions.resetResult.type,
    });
    getQuestions();
    setCurrentQuestionNo(1);
    startAssessment(quizGetAttemptData);
  };

  const restartSubmitHandler = () => {
    if (
      isLive &&
      frames &&
      quizData?.quizMetadata.failBehaviour === FailBehaviour.restart
    ) {
      startAssessment(quizGetAttemptData);
      dispatch({
        type: quizActions.resetResult.type,
      });
      dispatch({
        type: ElearningPlayerActions.enableQuiz.type,
        payload: false,
      });
      moveToFrame(frames[0].id);
    }
  };

  useEffect(() => {
    dispatch({
      type: quizActions.initQuiz.type,
      quizData,
    });
  }, []);

  if (!quizData) {
    return <div />;
  }

  const getRandomQuestion = (
    quizDataForQuestions: QuizDataType | undefined,
  ) => {
    if (!quizDataForQuestions || quizDataForQuestions.questions.length <= 0)
      return undefined;

    const questions = [...quizDataForQuestions.questions];
    if (questions.length === 1) return questions[0];

    // filter questions first
    const compulsoryQuestions = getCompulsoryQuestions(questions);

    // get random question
    return compulsoryQuestions.length > 0
      ? compulsoryQuestions[
          Math.round(Math.random() * (compulsoryQuestions.length - 1))
        ]
      : questions[Math.round(Math.random() * (questions.length - 1))];
  };

  const hasQuestionsToAsk = quizData.quizMetadata.questionNo > 0;
  const hasEnoughQuestions =
    getEnabledQuestions(quizData.questions).length >=
    quizData.quizMetadata.questionNo;
  const askEnoughQuestions =
    getCompulsoryQuestions(quizData.questions).length <=
    quizData.quizMetadata.questionNo;

  /* This is an unlikelly scenario, but placed incase we miss any edge cases. 
  Considering we are migrating from old course builder. 
  (Proper validation should be in place to prevent this in the first place) */
  if (!hasQuestionsToAsk || !hasEnoughQuestions || !askEnoughQuestions)
    return (
      <>
        {!hasQuestionsToAsk && (
          <Alert
            message="There are no questions to ask!"
            type="warning"
            isCloseable={false}
          />
        )}
        {!hasEnoughQuestions && hasQuestionsToAsk && (
          <Alert
            message="There are not enough questions! please enable/add more questions or lower number of questions to ask."
            type="warning"
            isCloseable={false}
          />
        )}
        {!askEnoughQuestions && hasQuestionsToAsk && (
          <Alert
            message="You have more compulsory questions than number of questions to ask. Decrease compulsory questions or increase number of questions to ask."
            type="warning"
            isCloseable={false}
          />
        )}
      </>
    );

  // @todo: Pass whole quiz instead of question and quizMeta
  return quiz.result === null ? (
    <QuizView
      question={getRandomQuestion(quizData)}
      handler={handler}
      quizMeta={{
        showFeedback: quizData?.quizMetadata.showAnswerFeedback,
        showCorrectAnswer: quizData?.quizMetadata.showCorrect,
        lcmsCompanyId: quizData?.lcmsReferenceIds.companyId,
        total: quizData?.quizMetadata.questionNo,
        currentNo: currentQuestionNo,
      }}
    />
  ) : (
    <QuizResult
      score={quiz.result.score}
      pass={quizData?.quizMetadata.pass}
      handler={{ tryAgainSubmitHandler, restartSubmitHandler }}
      content={{
        passContent: quizData.lmsFeedbackMetadata.lmsContent1,
        failContent: quizData.lmsFeedbackMetadata.lmsFailContent,
        retryContent: quizData.lmsFeedbackMetadata.lmsRetryContent,
      }}
      quizMetadata={quizData.quizMetadata}
      quizAttemptResponse={quizGetAttemptData}
    />
  );
};

export default Quiz;
